import { Controller } from "@hotwired/stimulus"
import { patch, post, destroy } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "requirementsButton",
    "storiesButton", 
    "statusBadge",
    "progressBar",
    "phaseList",
    "referenceCount",
    "documentList",
    "artifactList"
  ]
  
  static values = {
    projectId: Number,
    statusUrl: String,
    requirementsUrl: String,
    storiesUrl: String,
    resumeUrl: String
  }
  
  connect() {
    console.log("Feature project controller connected", this.projectIdValue)
    
    // Start polling for status updates if generation is in progress
    if (this.hasStatusBadgeTarget) {
      const status = this.statusBadgeTarget.textContent.toLowerCase()
      if (status.includes('generating') || status.includes('progress')) {
        this.startPolling()
      }
    }
  }
  
  disconnect() {
    this.stopPolling()
  }
  
  async generateRequirements(event) {
    event.preventDefault()
    
    if (!confirm("Generate feature requirements? This may take a few minutes.")) {
      return
    }
    
    const button = event.currentTarget
    button.disabled = true
    button.textContent = "Generating..."
    
    try {
      const response = await post(this.requirementsUrlValue, {
        responseKind: "json"
      })
      
      if (response.ok) {
        const data = await response.json
        this.showNotification("Requirements generation started", "success")
        this.startPolling()
        this.updateButton(button, "Generating Requirements...", true)
      } else {
        throw new Error("Failed to start generation")
      }
    } catch (error) {
      console.error("Error generating requirements:", error)
      this.showNotification("Failed to start requirements generation", "error")
      this.updateButton(button, "Generate Requirements", false)
    }
  }
  
  async generateStories(event) {
    event.preventDefault()
    
    if (!confirm("Generate user stories? This may take several minutes.")) {
      return
    }
    
    const button = event.currentTarget
    button.disabled = true
    button.textContent = "Generating..."
    
    // Collect generation options from form if present
    const form = button.closest('form')
    const formData = form ? new FormData(form) : new FormData()
    
    try {
      const response = await post(this.storiesUrlValue, {
        body: formData,
        responseKind: "json"
      })
      
      if (response.ok) {
        const data = await response.json
        this.showNotification("Story generation started", "success")
        this.startPolling()
        this.updateButton(button, "Generating Stories...", true)
      } else {
        const error = await response.json
        throw new Error(error.message || "Failed to start generation")
      }
    } catch (error) {
      console.error("Error generating stories:", error)
      this.showNotification(error.message || "Failed to start story generation", "error")
      this.updateButton(button, "Generate User Stories", false)
    }
  }
  
  async resumeGeneration(event) {
    event.preventDefault()
    
    const phaseName = event.currentTarget.dataset.phaseName
    
    if (!confirm(`Resume generation from ${phaseName} phase?`)) {
      return
    }
    
    try {
      const response = await post(this.resumeUrlValue, {
        body: JSON.stringify({ phase_name: phaseName }),
        responseKind: "json",
        contentType: "application/json"
      })
      
      if (response.ok) {
        const data = await response.json
        this.showNotification(data.message, "success")
        this.startPolling()
      } else {
        throw new Error("Failed to resume generation")
      }
    } catch (error) {
      console.error("Error resuming generation:", error)
      this.showNotification("Failed to resume generation", "error")
    }
  }
  
  startPolling() {
    if (this.polling) return
    
    this.polling = true
    this.pollStatus()
    this.pollInterval = setInterval(() => {
      this.pollStatus()
    }, 3000) // Poll every 3 seconds
  }
  
  stopPolling() {
    this.polling = false
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }
  
  async pollStatus() {
    if (!this.statusUrlValue) return
    
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateUI(data)
        
        // Stop polling if generation is complete
        if (data.status !== 'generating' && data.status !== 'in_progress') {
          this.stopPolling()
        }
      }
    } catch (error) {
      console.error("Error polling status:", error)
    }
  }
  
  updateUI(data) {
    // Update status badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = data.status.replace('_', ' ')
      
      // Update badge color based on status
      this.statusBadgeTarget.className = this.getStatusClass(data.status)
    }
    
    // Update progress bar if present
    if (this.hasProgressBarTarget && data.phase_counts) {
      const total = Object.values(data.phase_counts).reduce((sum, count) => sum + count, 0)
      const completed = data.phase_counts.completed || 0
      const percentage = total > 0 ? (completed / total * 100) : 0
      
      this.progressBarTarget.style.width = `${percentage}%`
      this.progressBarTarget.textContent = `${Math.round(percentage)}%`
    }
    
    // Update phase list if present
    if (this.hasPhaseListTarget && data.phase_counts) {
      Object.entries(data.phase_counts).forEach(([phase, count]) => {
        const phaseElement = this.phaseListTarget.querySelector(`[data-phase="${phase}"]`)
        if (phaseElement) {
          const countElement = phaseElement.querySelector('.phase-count')
          if (countElement) {
            countElement.textContent = count
          }
        }
      })
    }
    
    // Update button states
    if (data.requirements_complete) {
      if (this.hasRequirementsButtonTarget) {
        this.updateButton(this.requirementsButtonTarget, "Requirements Complete", true)
        this.requirementsButtonTarget.classList.add('btn-success')
      }
      
      if (this.hasStoriesButtonTarget) {
        this.updateButton(this.storiesButtonTarget, "Generate User Stories", false)
      }
    }
    
    // Update counts
    if (this.hasReferenceCountTarget) {
      const docCount = this.referenceCountTarget.dataset.documentCount || 0
      const artifactCount = this.referenceCountTarget.dataset.artifactCount || 0
      this.referenceCountTarget.textContent = `${docCount} documents, ${artifactCount} artifacts`
    }
  }
  
  getStatusClass(status) {
    const baseClasses = "badge badge-outline"
    
    switch(status) {
      case 'completed':
        return `${baseClasses} badge-success`
      case 'generating':
      case 'in_progress':
        return `${baseClasses} badge-warning`
      case 'failed':
        return `${baseClasses} badge-error`
      default:
        return baseClasses
    }
  }
  
  updateButton(button, text, disabled) {
    button.textContent = text
    button.disabled = disabled
  }
  
  showNotification(message, type = 'info') {
    // Create toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed bottom-4 right-4 z-50 max-w-md`
    toast.innerHTML = `
      <div>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.remove()
    }, 5000)
  }
  
  // Handle reference management
  async addDocumentReference(event) {
    event.preventDefault()
    
    const form = event.currentTarget.closest('form')
    const formData = new FormData(form)
    
    try {
      const response = await post(form.action, {
        body: formData,
        responseKind: "turbo-stream"
      })
      
      if (response.ok) {
        this.showNotification("Document reference added", "success")
        form.reset()
        
        // Close modal if present
        const modal = form.closest('dialog')
        if (modal) {
          modal.close()
        }
      }
    } catch (error) {
      console.error("Error adding document reference:", error)
      this.showNotification("Failed to add document reference", "error")
    }
  }
  
  async removeReference(event) {
    event.preventDefault()
    
    if (!confirm("Remove this reference?")) {
      return
    }
    
    const button = event.currentTarget
    const url = button.dataset.removeUrl
    
    try {
      const response = await destroy(url, {
        responseKind: "turbo-stream"
      })
      
      if (response.ok) {
        this.showNotification("Reference removed", "success")
        
        // Remove the element from DOM
        const referenceItem = button.closest('.reference-item')
        if (referenceItem) {
          referenceItem.remove()
        }
      }
    } catch (error) {
      console.error("Error removing reference:", error)
      this.showNotification("Failed to remove reference", "error")
    }
  }
  
  // Tab switching
  switchTab(event) {
    const tab = event.currentTarget
    const tabName = tab.dataset.tab
    
    // Update URL without page reload
    const url = new URL(window.location)
    url.searchParams.set('tab', tabName)
    window.history.pushState({}, '', url)
    
    // Update active tab styling
    document.querySelectorAll('.tab').forEach(t => {
      t.classList.remove('tab-active')
    })
    tab.classList.add('tab-active')
  }
}